import i18n from "@/i18n";
import AlarmsService from "../../services/AlarmsPageLogService";
import DateTimeHandlerService from "@/services/common/date-time/DateTimeHandlerService";
import SignalStatusEnum from "@/constants/enums/SignalStatusEnum";
import {
	SET_SEARCH,
	ON_EXPAND_TABLE,
	RESET_EXPANDED_ITEM,
	SET_SELECTED_ASSETS,
	SET_ALL_ASSETS_SELECTED,
	SET_SELECTED_STATUS,
	SET_SELECTED_MANAGEMENTS,
	SET_AVAILABLE_ASSETS,
	SET_AVAILABLE_STATUS,
	ON_TOGGLE_SELECT_FILTERED_ASSETS,
	UPDATE_MANAGEMENTS,
	UPLOAD_REPORT,
	FILTER_REPORT_ATTACHED_ITEMS,
	FILTER_DIMISSED_ITEMS,
	INITIALIZE,
	ON_TOGGLE_SELECT_ALL_ASSETS,
	ON_FILTER_CHANGE,
	SET_INITIAL_LOG_DATA,
	ON_DATE_CHANGE,
	ON_DATE_TIME_CHANGE,
} from "./AlarmsLogActionsType.js";
import {
	HIDE_TOAST_MESSAGE,
	SHOW_ERROR_MESSAGE,
	SHOW_SUCCESS_MESSAGE
} from "../AlarmsActionsType";
import {
	GET, SET, RESET_STATE
} from "@/stores/CommumActionTypes";
import { ALARMS_PATH } from "../ModulePath";
import CompaniesService from "@/services/common/CompaniesService";
import AlarmManagementStatus from "@/constants/enums/AlarmManagementStatus";

const actions = {
	/**
	 * Ação responsável por carregar dados iniciais para o sistema de alarmes.
	 * Realiza a definição do período inicial e dispara ações para carregar os dados e inicializar o sistema.
	 *
	 * @param {Object} context - O contexto Vuex, contendo as funções `dispatch` e `getters`.
	 * @param {Function} context.dispatch - Função do Vuex para disparar ações.
	 * @param {Object} context.getters - Função do Vuex para acessar os getters do estado.
	 *
	 * @returns {Promise} Promise que resolve quando todas as ações forem concluídas.
	 */
	async [SET_INITIAL_LOG_DATA]({
		dispatch, getters, rootState
	})
	{
		await dispatch(INITIALIZE);

		const selectedManagements = [ AlarmManagementStatus.NONE, AlarmManagementStatus.EXPIRED_QUARENTINE ];
		await dispatch(SET_SELECTED_MANAGEMENTS, selectedManagements);

		if (rootState.AlarmsLogModule.Log.availableAssets.length > 0)
		{
			await dispatch(SET_SELECTED_ASSETS, rootState.AlarmsLogModule.Log.availableAssets);
		}

		if (Array.isArray(getters.translatedAvailableStatus) && getters.translatedAvailableStatus.length > 0)
		{
			const selectedStatus = getters.translatedAvailableStatus.filter(
				status => status.id !== SignalStatusEnum.UNDEFINED
			);
			dispatch(SET_SELECTED_STATUS, selectedStatus);
		}
	},

	/**
	 * Define os logs fornecidos e guarda na store.
	 *
	 * @param {Object} context - O contexto Vuex.
	 * @param {Array} logs - Lista de logs.
	 */
	[SET]: ({ commit }, logs) =>
	{
		commit(SET, logs);
	},

	/**
	 * Busca os logs de configuração de limite de tendência de frequência na API,
	 * limitado pela empresa de quem faz a requisição.
	 *
	 * @param {Object} context - O contexto Vuex.
	 * da pesquisa.
	 */
	[GET]: async({
		commit, state, dispatch
	}) =>
	{
		try
		{
			commit(GET);

			const dateTime = state.dateTimeFilter;

			const startDateTime = DateTimeHandlerService.ToUTCDateTime(`${dateTime.startDate} ${dateTime.startTime}`);
			const endDateTime = DateTimeHandlerService.ToUTCDateTime(`${dateTime.endDate} ${dateTime.endTime}`);

			const selectedStatus = state.selectedStatus.map(status => status.id);
			const selectedMeIds = state.selectedAssets.map(asset => asset.id);
			const selectedManagements = state.selectedManagements;

			if (selectedMeIds.length === 0 || selectedStatus.length === 0)
			{
				commit(SET, []);
				return;
			}

			const logs = await AlarmsService.GetAlarmsLog(
				selectedMeIds,
				startDateTime,
				endDateTime,
				selectedStatus,
				selectedManagements
			);

			commit(RESET_EXPANDED_ITEM);
			commit(SET, logs);

			const statusDictionary = {
				[SignalStatusEnum.ALARM]: { id: SignalStatusEnum.ALARM },
				[SignalStatusEnum.TRIP]:  { id: SignalStatusEnum.TRIP },
			};

			const uniqueStatus = [
				...new Map(
					logs
						.map(log =>
						{
							const statusId = log.status;
							return statusDictionary[statusId];
						})
						.filter(Boolean)
						.map(status => [ status.id, status ])
				).values()
			];

			commit(SET_AVAILABLE_STATUS, uniqueStatus);
		}
		catch (error)
		{
			commit(RESET_EXPANDED_ITEM);
			commit(SET, []);

			const errorMessage = i18n.t("ErrorMessages.AlarmsLogError");
			commit(`${ALARMS_PATH}/${SHOW_ERROR_MESSAGE}`, errorMessage, { root: true });
		}
	},

	/**
	 * Define o que o usuário está digitando na procura da tabela.
	 *
	 * @param {Object} context - O contexto Vuex.
	 * @param {string} strToSearch - Entrada do usuário no formulário de busca.
	 */
	[SET_SEARCH]: ({ commit }, strToSearch) =>
	{
		commit(SET_SEARCH, strToSearch);
	},

	/**
	 * Define os valores do objeto da tabela expandida e se ela está ou não expandida.
	 *
	 * @param {Object} context - O contexto Vuex.
	 * @param {Object} event - Objeto do evento de click de expansão.
	 */
	[ON_EXPAND_TABLE]: ({ commit }, event) =>
	{
		commit(ON_EXPAND_TABLE, event);
	},

	/**
	 * Reseta o state desse modulo.
	 *
	 * @param {Object} context - O contexto Vuex.
	 */
	[RESET_STATE]: ({ commit }) =>
	{
		commit(RESET_STATE);
	},

	/**
	 * Reseta o item em expansão.
	 *
	 * @param {Object} context - O contexto Vuex.
	 */
	[RESET_EXPANDED_ITEM]: ({ commit }) =>
	{
		commit(RESET_EXPANDED_ITEM);
	},

	/**
   * Reseta o state desse módulo.
   * @param {Object} context - O contexto Vuex.
   */
	[RESET_STATE]: ({ commit }) =>
	{
		commit(RESET_STATE);
	},

	/**
	 * Define os assets selecionados no filtro.
	 *
	 * @param {Object} context - O contexto Vuex.
	 * @param {Array} selectedAssets - Lista de assets selecionados.
	 */
	[SET_SELECTED_ASSETS]: ({ commit }, selectedAssets) =>
	{
		commit(SET_SELECTED_ASSETS, selectedAssets);
	},

	[FILTER_REPORT_ATTACHED_ITEMS]: async({ commit, dispatch }, isFiltering) =>
	{
		if (isFiltering)
		{
			commit(FILTER_REPORT_ATTACHED_ITEMS, isFiltering);
		}
		else
		{
			await dispatch(GET);
		}
	},

	[FILTER_DIMISSED_ITEMS]: async({ commit, dispatch }, isFiltering) =>
	{
		if (isFiltering)
		{
			commit(FILTER_DIMISSED_ITEMS, isFiltering);
		}
		else
		{
			await dispatch(GET);
		}
	},

	[SET_ALL_ASSETS_SELECTED]: ({ commit }, allAssetsSelected) =>
	{
		commit(SET_ALL_ASSETS_SELECTED, allAssetsSelected);
	},
	/**
	 * Define os assets disponíveis para filtro.
	 * @param {Object} context - O contexto Vuex.
	 * @param {Array} availableAssets - Lista de assets únicos.
	 */
	[SET_AVAILABLE_ASSETS]: ({ commit }, availableAssets) =>
	{
		commit(SET_AVAILABLE_ASSETS, availableAssets);
	},

	/**
	 * Define os status selecionados no filtro.
	 *
	 * @param {Object} context - O contexto Vuex.
	 * @param {Array} selectedStatus - Lista de status selecionados.
	 */
	[SET_SELECTED_STATUS]: ({ commit }, selectedStatus) =>
	{
		commit(SET_SELECTED_STATUS, selectedStatus);
	},

	/**
	 * Define as tratativas selecionados no filtro.
	 *
	 * @param {Object} context - O contexto Vuex.
	 * @param {Array} selectedManagements - Lista de tratativas selecionadas.
	 */
	[SET_SELECTED_MANAGEMENTS]: ({ commit }, selectedManagements) =>
	{
		commit(SET_SELECTED_MANAGEMENTS, selectedManagements);
	},

	/**
   * Define os status disponíveis para filtro.
   * @param {Object} context - O contexto Vuex.
   * @param {Array} availableStatus - Lista de status únicos.
   */
	[SET_AVAILABLE_STATUS]: ({ commit }, availableStatus) =>
	{
		commit(SET_AVAILABLE_STATUS, availableStatus);
	},

	/**
	 * Chamada das turbinas (Generating Units) com base na empresa associada ao usuário.
	 *
	 * @param {Object} context - O contexto Vuex.
	 */
	[INITIALIZE]: async({ rootState, commit }) =>
	{
		const companiesId = rootState.UserModule.user?.companiesId;
		const availableAssets = await CompaniesService.GetCompanyGeneratingUnits(companiesId);
		commit(SET_AVAILABLE_ASSETS, availableAssets);
	},

	/**
	 * Alterna a seleção das turbinas filtradas pelo campo de texto.
	 * Se todos estiverem selecionados, desmarca; caso contrário, marca todos.
	 *
	 * @param {Object} context - O contexto Vuex, incluindo `commit`, `state` e `getters`.
	 */
	[ON_TOGGLE_SELECT_FILTERED_ASSETS]: ({
		commit, state, getters
	}) =>
	{
		const filtered = getters.filteredAssets;

		if (getters.allFilteredAssetsSelected)
		{
			const filteredNames = new Set(filtered.map(f => f.name));
			const remainingSelectedAssets = state.selectedAssets.filter(
				selected => !filteredNames.has(selected.name)
			);
			commit(SET_SELECTED_ASSETS, remainingSelectedAssets);
		}
		else
		{
			const selectedNames = new Set(state.selectedAssets.map(a => a.name));
			const newAssetsToSelect = filtered.filter(asset => !selectedNames.has(asset.name));
			const updatedSelectedAssets = [ ...state.selectedAssets, ...newAssetsToSelect ];
			commit(SET_SELECTED_ASSETS, updatedSelectedAssets);
		}
	},
	/**
	 * Atualiza os registros de gerenciamento de alarmes assincronamente.
	 * 
	 * @param {number} payload.notificationLoggingId - Id do alarme.
	 * @param {number} payload.management - status de gerenciamento para atualizar.
	 * @param {number|string} payload.expirationDate - Inteiro que representa a data de expiração da quarentena.
	 */
	async UPDATE_MANAGEMENTS({ commit, dispatch }, payload)
	{
		try
		{
			const resp = await AlarmsService.UpdateAlarmsManagements(
				payload.management,
				payload.expirationDate,
				payload.notificationLoggingId
			);
			const successMessage = i18n.t("ConfirmationMessages.UpdateAlarmNotications");
			commit(UPDATE_MANAGEMENTS, { resp, notificationLoggingId: payload.notificationLoggingId });
			dispatch(`AlarmsLogModule/${SHOW_SUCCESS_MESSAGE}`, successMessage, { root: true });
		}
		catch (error)
		{
			const errorMessage = `${i18n.t("ErrorMessages.BadDataError")} - ${error.message}`;
			dispatch(`AlarmsLogModule/${SHOW_ERROR_MESSAGE}`, errorMessage, { root: true });
		}
		finally
		{
			dispatch(`AlarmsLogModule/${HIDE_TOAST_MESSAGE}`, null, { root: true });
		}
	},

	/**
	 * Executa a requisição para download de um relatório.
	 * Retorna a resposta da API ou executa commit de uma mensagem de erro em caso de falha.
	 *
	 * @param {Object} context - Contexto da ação Vuex (ex: commit).
	 * @param {Object} file - Objeto com informações do arquivo para download.
	 * @returns {Promise<any>} Resposta da API de download.
	 */
	async DOWNLOAD_REPORT({ dispatch }, file)
	{
		try
		{
			const resp = await AlarmsService.DownloadReport(file);
			const successMessage = i18n.t("ConfirmationMessages.DownloadDocumentSuccess");
			dispatch(`${ALARMS_PATH}/${SHOW_SUCCESS_MESSAGE}`, successMessage, { root: true });
			return resp;
		}
		catch (error)
		{
			const errorMessage = `${i18n.t("ErrorMessages.UnknownError")} - ${error.message}`;
			dispatch(`${ALARMS_PATH}/${SHOW_ERROR_MESSAGE}`, errorMessage, { root: true });
		}
		finally
		{
			dispatch(`AlarmsLogModule/${HIDE_TOAST_MESSAGE}`, null, { root: true });
		}
	},

	/**
	 * Upload de um relatório.
	 *
	 * @param {Object} payload - Os dados do relatório para envio.
	 * @returns {Promise<Object|undefined>} A resposta da chamada de envio,
	 * ou undefined se ocorrer um erro.
	 */
	async UPLOAD_REPORT({ commit, dispatch }, payload)
	{
		try
		{
			const resp = await AlarmsService.UpdloadReport(payload);
			const successMessage = i18n.t("ConfirmationMessages.UploadDocumentSuccess");
			commit(UPLOAD_REPORT, resp);
			dispatch(`${ALARMS_PATH}/${SHOW_SUCCESS_MESSAGE}`, successMessage, { root: true });
			return resp;
		}
		catch (error)
		{
			const errorMessage = `${i18n.t("ErrorMessages.UnknownError")} - ${error.message}`;
			dispatch(`${ALARMS_PATH}/${SHOW_ERROR_MESSAGE}`, errorMessage, { root: true });
		}
		finally
		{
			dispatch(`AlarmsLogModule/${HIDE_TOAST_MESSAGE}`, null, { root: true });
		}
	},

	/**
	 * Alterna a seleção de todos as turbinas.
	 * Se todos estiverem selecionados, desmarca; caso contrário, marca todos.
	 *
	 * @param {Object} context - O contexto Vuex, incluindo `state` e `commit`.
	 */
	[ON_TOGGLE_SELECT_ALL_ASSETS]: ({ state, commit }) =>
	{
		const allSelected = state.selectedAssets.length === state.availableAssets.length;

		if (allSelected)
		{
			commit(SET_SELECTED_ASSETS, []);
		}
		else
		{
			commit(SET_SELECTED_ASSETS, [ ...state.availableAssets ]);
		}
	},

	/**
	 * Define o status selecionado no filtro dentre os disponíveis.
	 * @param {Object} context - O contexto Vuex.
	 * @param {string} statusValue - Valor do status.
	 */
	async [ON_FILTER_CHANGE]({ dispatch }, statusValue)
	{
		if (statusValue === null)
		{
			return;
		}

		dispatch(SET_SELECTED_STATUS, statusValue);
		await dispatch(GET);
	},

	/**
     * Transforma o numero de dias passado em um objeto DateTime e o define no estado.
     *
     * @param {Object} context - O contexto Vuex.
     * @param {number} daysInPast - Contém um inteiro que equivale a quantidade de dias no passado.
     */
	async [ON_DATE_CHANGE]({ commit }, daysInPast)
	{
		const dateTime = await DateTimeHandlerService.CreateInitialPeriod(daysInPast);

		commit(ON_DATE_CHANGE, dateTime);
	},

	/**
     * Salva a data e hora selecionadas no estado.
     *
     * @param {Object} context - O contexto Vuex.
     * @param {Object} dateTime - Contém as informações da data e hora já formatados.
     * @param {string} dateTime.startDate - A data de início (ex: "16/02/2023").
     * @param {string} dateTime.startTime - A hora de início (ex: "11:56").
     * @param {string} dateTime.endDate - A data de fim (ex: "24/06/2025").
     * @param {string} dateTime.endTime - A hora de fim (ex: "11:56").
     */
	async [ON_DATE_TIME_CHANGE]({ commit }, dateTime)
	{
		commit(ON_DATE_CHANGE, dateTime);
	},
};

export default { actions };
